import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Plus, Trash2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { uploadToFtp } from "../../../lib/uploadToFtp";
import UploadProgress from "../../../components/UploadProgress";

interface VideoItem {
  id: string;
  title: string;
  src: string;
  url?: string; // videos table uses url in Supabase
}

const AddBoat: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [availableVideos, setAvailableVideos] = useState<VideoItem[]>([]);
  
  // Extra Equipment Inputs
  const [newExtraName, setNewExtraName] = useState("");
  const [newExtraPrice, setNewExtraPrice] = useState("");
  
  const [formData, setFormData] = useState({
    Title: "",
    Description: "",
    FullInfo: "",
    Price: "",
    BoatLength: "",
    BoatWidth: "",
    BoatCapacity: "",
    BoatWeight: "",
    MinMotorPower: "",
    MaxMotorPower: "",
    DefaultEquipments: "", // comma separated
    ExtraEquipments: [] as { name: string; price: number }[],
    CatalogImage: "",
    Images: [] as string[],
    Videos: [] as string[],
  });

  const [catalogFile, setCatalogFile] = useState<File | null>(null);
  const [catalogPreview, setCatalogPreview] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideos = async () => {
        const { data, error } = await supabase.from('videos').select('*');
        if (data && !error) {
            const mapped = data.map(v => ({ id: v.id, title: v.title, src: v.url }));
            setAvailableVideos(mapped);
        }
    };
    fetchVideos();
  }, []);

  const handleCatalogChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setCatalogFile(file);
      setCatalogPreview(URL.createObjectURL(file));
    }
  };

  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);

  const handleGalleryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setGalleryFiles(prev => [...prev, ...files]);
      
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setGalleryPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const addExtraEquipment = () => {
      if (!newExtraName) return;
      const newExtra = {
          name: newExtraName,
          price: parseFloat(newExtraPrice) || 0
      };
      setFormData(prev => ({
          ...prev,
          ExtraEquipments: [...prev.ExtraEquipments, newExtra]
      }));
      setNewExtraName("");
      setNewExtraPrice("");
  };

  const removeExtraEquipment = (index: number) => {
      setFormData(prev => ({
          ...prev,
          ExtraEquipments: prev.ExtraEquipments.filter((_, i) => i !== index)
      }));
  };

  const toggleVideo = (videoSrc: string) => {
      setFormData(prev => {
          const exists = prev.Videos.includes(videoSrc);
          if (exists) {
              return { ...prev, Videos: prev.Videos.filter(v => v !== videoSrc) };
          } else {
              return { ...prev, Videos: [...prev.Videos, videoSrc] };
          }
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let catalogPath = formData.CatalogImage;

      if (catalogFile) {
         setUploading(true);
         setUploadProgress(0);
         setUploadFileName(catalogFile.name);
         catalogPath = await uploadToFtp(catalogFile, "boats/images", undefined, (p) => setUploadProgress(p));
      }

      // Upload Gallery Images
      const galleryUrls = [];
      const totalFiles = galleryFiles.length;
      for (let i = 0; i < galleryFiles.length; i++) {
         setUploadFileName(galleryFiles[i].name);
         setUploadProgress(0);
         setUploadCurrent(i + 1);
         setUploadTotal(totalFiles);
         const url = await uploadToFtp(galleryFiles[i], "boats/images", undefined, (p) => setUploadProgress(p));
         galleryUrls.push(url);
      }
      setUploading(false);

      const defaultEqArray = formData.DefaultEquipments.split(",").map(s => s.trim()).filter(s => s);

      const { error: insertError } = await supabase.from('boats').insert([{
          Title: formData.Title,
          Description: formData.Description,
          FullInfo: formData.FullInfo,
          Price: formData.Price,
          BoatLength: formData.BoatLength,
          BoatWidth: formData.BoatWidth,
          BoatCapacity: formData.BoatCapacity,
          BoatWeight: formData.BoatWeight,
          MinMotorPower: formData.MinMotorPower,
          MaxMotorPower: formData.MaxMotorPower,
          CatalogImage: catalogPath,
          DefaultEquipments: defaultEqArray,
          ExtraEquipments: formData.ExtraEquipments,
          Images: galleryUrls,
          Videos: formData.Videos
      }]);

      if (insertError) throw new Error("Tekne eklenemedi: " + insertError.message);
      
      toast.success("Tekne başarıyla eklendi");
      navigate("/panel/boats");
      
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center space-x-4 mb-6">
        <button onClick={() => navigate("/panel/boats")} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Yeni Tekne Ekle</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {uploading && <UploadProgress progress={uploadProgress} fileName={uploadFileName} currentFile={uploadCurrent} totalFiles={uploadTotal} />}
          {/* Catalog Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Katalog Görseli</label>
             <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors cursor-pointer relative"
                 onClick={() => document.getElementById('catalog-upload')?.click()}>
                {catalogPreview ? (
                    <img src={catalogPreview} alt="Katalog önizleme" className="h-64 object-contain" />
                ) : (
                    <div className="text-center"><Upload className="mx-auto h-12 w-12 text-gray-400"/><span>Resim Seç</span></div>
                )}
                <input id="catalog-upload" type="file" className="sr-only" accept="image/*" onChange={handleCatalogChange} />
             </div>
          </div>

          {/* Gallery Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Galeri Görselleri</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {galleryPreviews.map((src, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                        <img src={src} alt={`Galeri ${idx}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeGalleryImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors">
                            <X size={16} />
                        </button>
                    </div>
                ))}
                <div 
                    className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors"
                    onClick={() => document.getElementById('gallery-upload')?.click()}
                >
                    <Plus className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-500">Ekle</span>
                </div>
            </div>
            <input id="gallery-upload" type="file" multiple className="sr-only" accept="image/*" onChange={handleGalleryChange} />
          </div>

          <div className="grid grid-cols-2 gap-4">
             <div><label className="block text-sm font-medium mb-1">Model Adı</label><input required className="w-full border rounded p-2" value={formData.Title} onChange={e => setFormData({...formData, Title: e.target.value})} /></div>
             <div><label className="block text-sm font-medium mb-1">Fiyat</label><input required className="w-full border rounded p-2" value={formData.Price} onChange={e => setFormData({...formData, Price: e.target.value})} /></div>
          </div>
          
          <div><label className="block text-sm font-medium mb-1">Kısa Açıklama</label><input required className="w-full border rounded p-2" value={formData.Description} onChange={e => setFormData({...formData, Description: e.target.value})} /></div>
          <div><label className="block text-sm font-medium mb-1">Detaylı Bilgi</label><textarea required rows={4} className="w-full border rounded p-2" value={formData.FullInfo} onChange={e => setFormData({...formData, FullInfo: e.target.value})} /></div>

          <h3 className="font-bold text-lg border-b pb-2 mt-6">Teknik Özellikler</h3>
          <div className="grid grid-cols-2 gap-4">
             <div><label className="block text-sm font-medium mb-1">Uzunluk</label><input className="w-full border rounded p-2" value={formData.BoatLength} onChange={e => setFormData({...formData, BoatLength: e.target.value})} /></div>
             <div><label className="block text-sm font-medium mb-1">Genişlik</label><input className="w-full border rounded p-2" value={formData.BoatWidth} onChange={e => setFormData({...formData, BoatWidth: e.target.value})} /></div>
             <div><label className="block text-sm font-medium mb-1">Kapasite</label><input className="w-full border rounded p-2" value={formData.BoatCapacity} onChange={e => setFormData({...formData, BoatCapacity: e.target.value})} /></div>
             <div><label className="block text-sm font-medium mb-1">Ağırlık</label><input className="w-full border rounded p-2" value={formData.BoatWeight} onChange={e => setFormData({...formData, BoatWeight: e.target.value})} /></div>
             <div><label className="block text-sm font-medium mb-1">Min Motor</label><input className="w-full border rounded p-2" value={formData.MinMotorPower} onChange={e => setFormData({...formData, MinMotorPower: e.target.value})} /></div>
             <div><label className="block text-sm font-medium mb-1">Max Motor</label><input className="w-full border rounded p-2" value={formData.MaxMotorPower} onChange={e => setFormData({...formData, MaxMotorPower: e.target.value})} /></div>
          </div>

          <div><label className="block text-sm font-medium mb-1">Standart Donanım (Virgülle ayırın)</label><textarea rows={3} className="w-full border rounded p-2" value={formData.DefaultEquipments} onChange={e => setFormData({...formData, DefaultEquipments: e.target.value})} /></div>
          
          {/* Extra Equipment Section */}
          <div>
                <label className="block text-sm font-medium mb-1">Ekstra Donanım (Özelleştirilebilir)</label>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="space-y-2 mb-4">
                        {formData.ExtraEquipments.map((extra, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-white p-2 border rounded shadow-sm">
                                <span className="flex-1 font-medium">{extra.name}</span>
                                <span className="text-gray-500 font-mono text-sm">{extra.price} ₺</span>
                                <button type="button" onClick={() => removeExtraEquipment(idx)} className="text-red-500 hover:text-red-700">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input 
                            placeholder="Donanım Adı" 
                            className="flex-1 border rounded p-2" 
                            value={newExtraName} 
                            onChange={e => setNewExtraName(e.target.value)} 
                        />
                        <input 
                            placeholder="Fiyat (TL)" 
                            type="number" 
                            className="w-24 border rounded p-2" 
                            value={newExtraPrice} 
                            onChange={e => setNewExtraPrice(e.target.value)} 
                        />
                        <button type="button" onClick={addExtraEquipment} className="bg-green-600 text-white px-4 rounded hover:bg-green-700">
                            <Plus />
                        </button>
                    </div>
                </div>
            </div>

            {/* Video Selection Section */}
            <div>
                <label className="block text-sm font-medium mb-2">Videolar</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-60 overflow-y-auto p-2 border rounded">
                    {availableVideos.map(video => (
                        <div 
                            key={video.id} 
                            className={`cursor-pointer border-2 rounded-lg overflow-hidden relative ${formData.Videos.includes(video.src) ? 'border-blue-500' : 'border-transparent'}`}
                            onClick={() => toggleVideo(video.src)}
                        >
                            <video src={video.src} className="w-full h-24 object-cover pointer-events-none" />
                            <div className="p-1 text-xs truncate bg-white">{video.title}</div>
                            {formData.Videos.includes(video.src) && (
                                <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center text-xs">✓</div>
                            )}
                        </div>
                    ))}
                    {availableVideos.length === 0 && <div className="col-span-4 text-center text-gray-500 text-sm">Video bulunamadı. Önce video yükleyiniz.</div>}
                </div>
            </div>

          <div className="flex justify-end pt-4">
            <button type="submit" disabled={loading} className="bg-blue-600 text-white font-bold py-3 px-8 rounded-lg">
              {loading ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBoat;
